import { useState } from 'react';
import { newsfeeds as Api, type NewsProvider, type NewsItem } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonPage } from '../components/Shimmer';
import { Field, Row, SecretInput } from '../components/Field';

const when = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? '' : 'Z'));
  return isNaN(d.valueOf()) ? v
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/* Where live market news comes from: Drishti for Indian equities, FMP for
   forex. The desk can connect and preview a provider today; members see it
   only once the provider's display licence is on file, which is decided
   here too, on purpose, beside the key that would otherwise just work. */
export function NewsFeeds() {
  const { data, error, loading, reload } = useAsync((signal) => Api.load(signal));
  if (loading && !data) return <SkeletonPage />;
  if (error) return <div className="pg"><div className="err">Could not load the news providers — {error.message}</div></div>;
  if (!data) return null;
  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>News feeds</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7, maxWidth: 720 }}>
            Live market news for members: Indian market from Drishti, forex from Financial Modeling Prep.
            Connect and preview here now. Members see a provider only after its display licence is on file
            and <strong>Show to members</strong> is on.
          </p>
        </div>
      </div>
      {!data.can_store_secrets ? (
        <div className="err">TVX_SECRET is not set on the server, so API keys cannot be stored safely.</div>
      ) : null}
      {data.providers.map((p) => <ProviderCard key={p.provider} p={p} onSaved={reload} canStore={data.can_store_secrets} />)}
    </div>
  );
}

function ProviderCard({ p, onSaved, canStore }: { p: NewsProvider; onSaved: () => Promise<unknown> | void; canStore: boolean }) {
  const [f, setF] = useState<NewsProvider>(p);
  const [key, setKey] = useState('');
  const [status, setStatus] = useState('');
  const [err, setErr] = useState('');
  const [feed, setFeed] = useState(p.feeds[0]?.key || '');
  const [items, setItems] = useState<NewsItem[] | null>(null);

  const opt = (k: keyof NewsProvider['options'], v: unknown) => setF({ ...f, options: { ...f.options, [k]: v } });
  const toggleFeed = (k: string) => opt('feeds', f.options.feeds.includes(k)
    ? f.options.feeds.filter((x) => x !== k) : [...f.options.feeds, k]);
  const licenceReady = f.licence_on_file && !!f.licence_ref.trim();
  const id = (s: string) => `${p.provider}-${s}`;

  async function save() {
    setErr('');
    setStatus('Saving…');
    try {
      const out = await Api.save(p.provider, {
        enabled: f.enabled, members: f.members && licenceReady,
        licence_on_file: f.licence_on_file, licence_ref: f.licence_ref, licence_date: f.licence_date,
        options: f.options, ...(key ? { api_key: key } : {}),
      });
      setF(out.provider);
      setKey('');
      setStatus('Saved');
      await onSaved();
    } catch (e) { setStatus(''); setErr((e as Error).message); }
  }

  async function test() {
    setErr('');
    setStatus('Testing…');
    try {
      const out = await Api.test(p.provider);
      setF({ ...f, tested_ok: out.provider.tested_ok, tested_at: out.provider.tested_at, last_error: '' });
      setItems(out.items);
      setStatus(`Connected — ${out.items.length} item${out.items.length === 1 ? '' : 's'} returned.`);
    } catch (e) { setStatus(''); setErr((e as Error).message); }
  }

  async function preview() {
    setErr('');
    setStatus('Fetching…');
    try {
      setItems((await Api.preview(p.provider, feed)).items);
      setStatus('');
    } catch (e) { setStatus(''); setErr((e as Error).message); }
  }

  const tag = !p.has_key ? ['No key', 'rgba(var(--goldrgb), .14)']
    : p.tested_ok ? ['Connected', 'rgba(62,207,142,.14)']
      : p.last_error ? ['Test failed', 'rgba(240,100,91,.13)'] : ['Not tested', 'rgba(var(--goldrgb), .14)'];

  return (
    <div className="crd" style={{ padding: '20px 22px', marginTop: 18 }}>
      <div className="rowbar" style={{ marginBottom: 14 }}>
        <div>
          <div className="kh">{p.market} · {p.label}</div>
          <a href={p.site} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--tx3)' }}>{p.site.replace(/^https:\/\//, '')}</a>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span className="tg" style={{ background: tag[1] }}>{tag[0]}</span>
          <span className="tg" style={{ background: p.members ? 'rgba(62,207,142,.14)' : 'var(--card2)' }}>
            {p.members ? 'Shown to members' : p.licence_on_file ? 'Members: off' : 'Members: licence pending'}
          </span>
        </div>
      </div>

      {err ? <div className="err">{err}</div> : null}

      <Row cols="2fr 1fr">
        <Field label="API key" hint={p.has_key
          ? (p.key_readable ? `Stored (${p.key_hint}). Leave blank to keep it.` : 'Stored but unreadable — the server key changed. Enter it again.')
          : `From your ${p.label} account. Stored encrypted.`}>
          <SecretInput disabled={!canStore} value={key} placeholder={p.has_key ? 'Unchanged' : ''} onChange={setKey} />
        </Field>
        <div className="fld" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 22 }}>
          <input id={id('enabled')} type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} />
          <label htmlFor={id('enabled')} style={{ fontSize: 13.5 }}>Feed on</label>
        </div>
      </Row>

      <div className="lbl" style={{ marginTop: 6 }}>What to pull</div>
      <div style={{ display: 'grid', gap: 8, margin: '8px 0 14px' }}>
        {p.feeds.map((x) => (
          <label key={x.key} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13.5 }}>
            <input type="checkbox" checked={f.options.feeds.includes(x.key)} onChange={() => toggleFeed(x.key)} />
            {x.label}
          </label>
        ))}
      </div>

      {p.provider === 'drishti' ? (
        <Row cols="2fr 1fr">
          <Field label="Stocks to follow" hint="NSE symbols, comma-separated, e.g. RELIANCE, TCS, HDFCBANK. Blank means the whole market. Starter covers 100 symbols on the live stream, Pro 1,000.">
            <textarea id={id('symbols')} className="inp" rows={2} value={f.options.symbols || ''}
              onChange={(e) => opt('symbols', e.target.value)} />
          </Field>
          <div className="fld" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 22 }}>
            <input id={id('important')} type="checkbox" checked={!!f.options.important_only}
              onChange={(e) => opt('important_only', e.target.checked)} />
            <label htmlFor={id('important')} style={{ fontSize: 13.5 }}>Important alerts and announcements only</label>
          </div>
        </Row>
      ) : (
        <Row cols={2}>
          <Field label="Calendar countries" hint="Two-letter codes, e.g. US, IN, EU, GB, JP.">
            <input id={id('countries')} className="inp" value={f.options.countries || ''} onChange={(e) => opt('countries', e.target.value)} />
          </Field>
          <Field label="Calendar events of at least" hint="High keeps it to CPI, NFP, central-bank decisions and the like.">
            <select id={id('impact')} className="inp" value={f.options.min_impact || 'High'} onChange={(e) => opt('min_impact', e.target.value)}>
              <option value="High">High impact</option>
              <option value="Medium">Medium impact</option>
              <option value="Low">Any impact</option>
            </select>
          </Field>
        </Row>
      )}

      <div className="crd" style={{ padding: '14px 16px', margin: '6px 0 16px', background: 'var(--card2)' }}>
        <div className="lbl">Display licence</div>
        <p className="hint" style={{ margin: '4px 0 10px' }}>
          API access alone does not allow showing {p.label}&apos;s content to paying members or sending it as push
          notifications. Record the signed licence or written confirmation here before switching members on.
        </p>
        <Row cols="auto 2fr 1fr">
          <div className="fld" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 22 }}>
            <input id={id('licence')} type="checkbox" checked={f.licence_on_file}
              onChange={(e) => setF({ ...f, licence_on_file: e.target.checked, members: e.target.checked ? f.members : false })} />
            <label htmlFor={id('licence')} style={{ fontSize: 13.5 }}>Licence on file</label>
          </div>
          <Field label="Reference" hint="Agreement number, or the email that confirms it.">
            <input id={id('ref')} className="inp" value={f.licence_ref} onChange={(e) => setF({ ...f, licence_ref: e.target.value })} />
          </Field>
          <Field label="Dated">
            <input id={id('date')} className="inp" type="date" value={f.licence_date} onChange={(e) => setF({ ...f, licence_date: e.target.value })} />
          </Field>
        </Row>
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13.5, opacity: licenceReady ? 1 : 0.55 }}>
          <input id={id('members')} type="checkbox" disabled={!licenceReady} checked={f.members && licenceReady}
            onChange={(e) => setF({ ...f, members: e.target.checked })} />
          Show to members
          {!licenceReady ? <span className="hint" style={{ margin: 0 }}>— needs the licence and its reference first</span> : null}
        </label>
      </div>

      <p className="hint" style={{ marginBottom: 12 }}>
        {p.tested_ok ? `Tested ${when(p.tested_at)} — it worked.`
          : p.last_error ? <span style={{ color: 'var(--down)' }}>Last test failed: {p.last_error}</span>
            : 'Never tested.'}
      </p>

      <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="b bo" type="button" disabled={!p.has_key} onClick={() => void test()}>Test connection</button>
        <select className="inp" style={{ maxWidth: 260 }} value={feed} onChange={(e) => setFeed(e.target.value)} aria-label="Feed to preview">
          {p.feeds.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        <button className="b bo" type="button" disabled={!p.has_key} onClick={() => void preview()}>Preview</button>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
        <button className="b bg" type="button" onClick={() => void save()}>Save</button>
      </div>

      {items ? (
        <div style={{ marginTop: 16 }}>
          <div className="lbl" style={{ marginBottom: 8 }}>Preview — visible to the desk only</div>
          {items.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>Nothing returned for these settings.</p> : (
            <div className="tblx">
              <table className="tbl">
                <thead><tr><th>When</th><th>Item</th><th>Symbol</th><th>Source</th></tr></thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i}>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{when(it.at)}</td>
                      <td>
                        {it.url ? <a href={it.url} target="_blank" rel="noreferrer">{it.title}</a> : it.title}
                        {it.summary ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{it.summary.slice(0, 220)}</div> : null}
                        {it.tag ? <span className="tg" style={{ marginTop: 5, display: 'inline-block' }}>{it.tag}</span> : null}
                      </td>
                      <td className="muted">{it.symbol}</td>
                      <td className="muted">{it.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
